import { Link } from 'react-router-dom';
import { useMemo } from 'react';
import { useMenuVisibility, type MenuItemKey } from '../../hooks/useMenuVisibility';
import { SITE_LOGO } from '../../constants/assets';
import { ADMIN_WHATSAPP } from '../../utils/whatsapp';

const ALL_NAV_COLUMNS = [
  {
    title: 'Servicios',
    links: [
      { label: 'Alta Costura', path: '/alta-costura', key: 'alta-costura' as MenuItemKey },
      { label: 'Vestidos de Novia', path: '/vestidos-novia', key: 'vestidos-novia' as MenuItemKey },
      { label: 'Trajes a Medida', path: '/trajes-medida', key: 'trajes-medida' as MenuItemKey },
      { label: 'Prêt-à-porter', path: '/pret-a-porter', key: 'pret-a-porter' as MenuItemKey },
      { label: 'Tienda', path: '/tienda', key: 'tienda' as MenuItemKey },
      { label: 'Mi Reserva', path: '/mi-reserva', key: 'mi-reserva' as MenuItemKey },
    ],
  },
  {
    title: 'Compañía',
    links: [
      { label: 'Sobre Nosotros', path: '/sobre-nosotros', key: 'sobre-nosotros' as MenuItemKey },
      { label: 'Blog', path: '/blog', key: 'blog' as MenuItemKey },
      { label: 'Testimonios', path: '/testimonios', key: 'testimonios' as MenuItemKey },
      { label: 'Contacto', path: '/contacto', key: 'contacto' as MenuItemKey },
    ],
  },
  {
    title: 'Reservas',
    links: [
      { label: 'Cita Privada', path: '/alta-costura', key: 'alta-costura' as MenuItemKey },
      { label: 'Arreglos', path: '/pret-a-porter', key: 'pret-a-porter' as MenuItemKey },
      { label: 'Consulta Online', path: '/contacto', key: 'contacto' as MenuItemKey },
      { label: 'Experiencia', path: '/experiencia', key: 'experiencia' as MenuItemKey },
    ],
  },
];

export default function Footer() {
  const { state: visibility, loading } = useMenuVisibility();

  const navColumns = useMemo(
    () =>
      ALL_NAV_COLUMNS.map((col) => ({
        ...col,
        links: col.links.filter((link) => visibility.footer[link.key] !== false),
      })).filter((col) => col.links.length > 0),
    [visibility.footer]
  );

  return (
    <footer className="w-full bg-brand-gray-50 py-16 md:py-20">
      <div className="px-6 lg:px-[4%]">
        <div className="bg-white rounded-[32px] px-8 md:px-16 py-12 md:py-16 relative overflow-hidden">
          {/* Content */}
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-8">
            {/* Brand Column */}
            <div className="lg:w-[25%]">
              <img
                src={SITE_LOGO}
                alt="Anthony Sánchez Couture"
                className="h-12 w-auto object-contain"
              />
              <p className="font-sans text-brand-gray-500 text-sm leading-[1.7] mt-5 max-w-xs">
                Alta costura artesanal y exclusiva en Barcelona. Cada prenda es una obra única hecha con pasión, ilusión y dedicación.
              </p>
            </div>

            {/* Navigation Columns */}
            {!loading && navColumns.map((col) => (
              <div key={col.title} className="lg:w-[18%]">
                <h4 className="font-sans text-brand-black text-[11px] tracking-[0.2em] uppercase font-bold">
                  {col.title}
                </h4>
                <ul className="mt-5 space-y-3.5">
                  {col.links.map((link) => (
                    <li key={link.label}>
                      <Link
                        to={link.path}
                        className="font-sans text-brand-gray-600 text-[15px] hover:text-brand-black transition-colors duration-200"
                      >
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}

            {/* Contact Column */}
            <div className="lg:w-[21%]">
              <h4 className="font-sans text-brand-black text-[11px] tracking-[0.2em] uppercase font-bold">
                Contacto
              </h4>
              <div className="mt-5 space-y-3.5">
                <p className="font-sans text-brand-gray-600 text-[15px]">
                  Rambla de Catalunya, 85
                  <br />
                  Eixample, Barcelona, España
                </p>
                <a
                  href="https://maps.app.goo.gl/GSn4KTcECpHm5Q9u9"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-sans text-brand-gray-600 text-[15px] hover:text-brand-black transition-colors duration-200 inline-block"
                >
                  Ver en Google Maps
                </a>
                <p className="font-sans text-brand-gray-600 text-[15px]">
                  Atelier abierto desde 2017
                </p>
              </div>
            </div>
          </div>

          {/* Copyright */}
          <div className="mt-12 pt-8 border-t border-brand-gray-200 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="font-sans text-brand-gray-400 text-[13px]">
              © 2025 Anthony Sánchez Couture. Todos los derechos reservados.
              <span className="hidden sm:inline"> | </span>
              <Link to="/" className="hover:text-brand-black transition-colors">
                Política de Privacidad
              </Link>
              <span className="hidden sm:inline"> | </span>
              <Link to="/admin" className="hover:text-brand-black transition-colors">
                Admin
              </Link>
            </div>

            <div className="flex gap-4">
              <a
                href="https://www.instagram.com/anthony_sanchez_couture"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-brand-black flex items-center justify-center text-white hover:bg-brand-gray-600 transition-colors duration-300"
                aria-label="Instagram"
              >
                <i className="ri-instagram-line text-lg" />
              </a>
              <a
                href="https://www.tiktok.com/@anthony_sanchez_couture"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-brand-black flex items-center justify-center text-white hover:bg-brand-gray-600 transition-colors duration-300"
                aria-label="TikTok"
              >
                <i className="ri-tiktok-line text-lg" />
              </a>
              <a
                href={`https://wa.me/${ADMIN_WHATSAPP}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-brand-black flex items-center justify-center text-white hover:bg-brand-gray-600 transition-colors duration-300"
                aria-label="WhatsApp"
              >
                <i className="ri-whatsapp-line text-lg" />
              </a>
              <a
                href="https://www.airbnb.es/users/profile/1715772313040186220"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-brand-black flex items-center justify-center text-white hover:bg-brand-gray-600 transition-colors duration-300"
                aria-label="Airbnb"
              >
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-[18px] h-[18px]" aria-hidden="true">
                  <path d="M12.001 18.275c-1.353-1.697-2.148-3.184-2.413-4.457-.263-1.027-.16-1.848.291-2.465.477-.71 1.188-1.056 2.121-1.056s1.643.345 2.12 1.063c.446.61.558 1.432.286 2.465-.291 1.298-1.085 2.785-2.412 4.458zm9.601 1.14c-.185 1.246-1.034 2.28-2.2 2.783-2.253.98-4.483-.583-6.392-2.704 3.157-3.951 3.74-7.028 2.385-9.018-.795-1.14-1.933-1.695-3.394-1.695-2.944 0-4.563 2.49-3.927 5.382.37 1.565 1.352 3.343 2.917 5.332-.98 1.085-1.91 1.856-2.732 2.333-.636.344-1.245.558-1.828.609-2.679.399-4.778-2.2-3.825-4.88.132-.345.395-.98.845-1.961l.025-.053c1.464-3.178 3.242-6.79 5.285-10.795l.053-.132.58-1.116c.45-.822.635-1.19 1.351-1.643.346-.21.77-.315 1.246-.315.954 0 1.698.558 2.016 1.007.158.239.345.557.582.953l.558 1.089.08.159c2.041 4.004 3.821 7.608 5.279 10.794l.026.025.533 1.22.318.764c.243.613.294 1.222.213 1.858zm1.22-2.39c-.186-.583-.505-1.271-.9-2.094v-.03c-1.889-4.006-3.642-7.608-5.307-10.844l-.111-.163C15.317 1.461 14.468 0 12.001 0c-2.44 0-3.476 1.695-4.535 3.898l-.081.16c-1.669 3.236-3.421 6.843-5.303 10.847v.053l-.559 1.22c-.21.504-.317.768-.345.847C-.172 20.74 2.611 24 5.98 24c.027 0 .132 0 .265-.027h.372c1.75-.213 3.554-1.325 5.384-3.317 1.829 1.989 3.635 3.104 5.382 3.317h.372c.133.027.239.027.265.027 3.37.003 6.152-3.261 4.802-6.975z" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}